import { NativeHealthState } from "@/utils/healthBridgeSchemas";
import { HealthPocMetric, HealthPocViewModel } from "@/utils/healthPocViewAdapter";

export type HealthWorkout = NonNullable<NativeHealthState["summary"]>["workouts"][number];
export interface HealthMetricCardData {
  metric: HealthPocMetric;
  icon: string;
  tone: string;
  valueLabel: string;
  unitLabel: string;
  caption: string;
}

export const emptyMetric = (key: string, label: string, unit: string | null): HealthPocMetric => ({
  key,
  label,
  value: 0,
  unit,
  available: false,
  measuredAt: null
});

export const metricByKey = (view: HealthPocViewModel, key: string): HealthPocMetric | undefined =>
  view.sections.reduce(
    (found, section) => found || section.metrics.find(metric => metric.key === key),
    undefined as HealthPocMetric | undefined
  );

export const formatNumber = (value: number, maximumFractionDigits = 1): string =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits }).format(value || 0);

export const formatDuration = (minutes: number): string => {
  const safeMinutes = Math.max(0, Math.floor(minutes || 0));
  const hours = Math.floor(safeMinutes / 60);
  return `${hours}h ${String(safeMinutes % 60).padStart(2, "0")}min`;
};

export const formatMetricValue = (metric: HealthPocMetric): string => {
  if (metric.key === "distance" && metric.unit === "m") {
    const kilometers = metric.value / 1000;
    return kilometers === 0 ? "0" : formatNumber(kilometers, 1);
  }
  if (["steps", "energy", "heart", "resting", "oxygen", "weight"].includes(metric.key)) {
    return formatNumber(metric.value, metric.key === "weight" ? 1 : 0);
  }
  return formatNumber(metric.value, 1);
};

export const formatMetricUnit = (metric: HealthPocMetric): string => {
  if (metric.key === "distance") return "km";
  if (metric.key === "sleep" || metric.key === "activeMinutes" || metric.key === "steps") return "";
  return metric.unit || "";
};

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return date.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export const measuredAtLabel = (metric: HealthPocMetric): string =>
  metric.measuredAt ? `Registro de ${formatDateTime(metric.measuredAt)}` : "Registro disponível";

const workoutActivityLabels: Record<string, string> = {
  AMERICAN_FOOTBALL: "Futebol americano",
  ARCHERY: "Tiro com arco",
  AUSTRALIAN_FOOTBALL: "Futebol australiano",
  BADMINTON: "Badminton",
  BASEBALL: "Beisebol",
  BASKETBALL: "Basquete",
  BIKING: "Ciclismo",
  CYCLING: "Ciclismo",
  BOXING: "Boxe",
  CARDIO_DANCE: "Dança aeróbica",
  CRICKET: "Críquete",
  CROSS_COUNTRY_SKIING: "Esqui cross-country",
  CURLING: "Curling",
  DOWNHILL_SKIING: "Esqui alpino",
  ELLIPTICAL: "Elíptico",
  FENCING: "Esgrima",
  GOLF: "Golfe",
  GYMNASTICS: "Ginástica",
  HANDBALL: "Handebol",
  HIGH_INTENSITY_INTERVAL_TRAINING: "Treino intervalado (HIIT)",
  HIKING: "Trilha",
  HOCKEY: "Hóquei",
  JUMP_ROPE: "Pular corda",
  KICKBOXING: "Kickboxing",
  MARTIAL_ARTS: "Artes marciais",
  PILATES: "Pilates",
  RACQUETBALL: "Raquetebol",
  ROWING: "Remo",
  RUGBY: "Rúgbi",
  RUNNING: "Corrida",
  SAILING: "Vela",
  SKATING: "Patinação",
  SNOWBOARDING: "Snowboard",
  SOCCER: "Futebol",
  SOFTBALL: "Softbol",
  SQUASH: "Squash",
  STAIR_CLIMBING: "Subida de escadas",
  SWIMMING: "Natação",
  TABLE_TENNIS: "Tênis de mesa",
  TENNIS: "Tênis",
  VOLLEYBALL: "Vôlei",
  WALKING: "Caminhada",
  WATER_POLO: "Polo aquático",
  YOGA: "Yoga",
  BARRE: "Barre",
  BOWLING: "Boliche",
  CLIMBING: "Escalada",
  COOLDOWN: "Desaceleração",
  CORE_TRAINING: "Treino de core",
  CROSS_TRAINING: "Treino cruzado",
  DISC_SPORTS: "Esportes com disco",
  EQUESTRIAN_SPORTS: "Esportes equestres",
  FISHING: "Pesca",
  FITNESS_GAMING: "Jogos de exercício",
  FLEXIBILITY: "Flexibilidade",
  FUNCTIONAL_STRENGTH_TRAINING: "Treino de força funcional",
  HAND_CYCLING: "Ciclismo com as mãos",
  HUNTING: "Caça",
  LACROSSE: "Lacrosse",
  MIND_AND_BODY: "Corpo e mente",
  MIXED_CARDIO: "Cardio misto",
  PADDLE_SPORTS: "Esportes com remo",
  PICKLEBALL: "Pickleball",
  PLAY: "Recreação",
  PREPARATION_AND_RECOVERY: "Preparação e recuperação",
  SNOW_SPORTS: "Esportes na neve",
  SOCIAL_DANCE: "Dança social",
  STAIRS: "Escadas",
  STEP_TRAINING: "Step",
  SURFING: "Surfe",
  TAI_CHI: "Tai chi",
  TRACK_AND_FIELD: "Atletismo",
  TRADITIONAL_STRENGTH_TRAINING: "Treino de força",
  WATER_FITNESS: "Hidroginástica",
  WATER_SPORTS: "Esportes aquáticos",
  WHEELCHAIR_RUN_PACE: "Corrida em cadeira de rodas",
  WHEELCHAIR_WALK_PACE: "Caminhada em cadeira de rodas",
  WRESTLING: "Luta",
  UNDERWATER_DIVING: "Mergulho",
  BIKING_STATIONARY: "Bicicleta ergométrica",
  CALISTHENICS: "Calistenia",
  DANCING: "Dança",
  FRISBEE_DISC: "Frisbee",
  GUIDED_BREATHING: "Respiração guiada",
  ICE_SKATING: "Patinação no gelo",
  PARAGLIDING: "Parapente",
  ROCK_CLIMBING: "Escalada",
  ROWING_MACHINE: "Remo ergométrico",
  RUNNING_TREADMILL: "Corrida na esteira",
  SCUBA_DIVING: "Mergulho",
  SKIING: "Esqui",
  SNOWSHOEING: "Caminhada na neve",
  STAIR_CLIMBING_MACHINE: "Simulador de escadas",
  STRENGTH_TRAINING: "Treino de força",
  SWIMMING_OPEN_WATER: "Natação em águas abertas",
  SWIMMING_POOL: "Natação na piscina",
  WALKING_TREADMILL: "Caminhada na esteira",
  WEIGHTLIFTING: "Musculação",
  WHEELCHAIR: "Atividade em cadeira de rodas",
  OTHER: "Outra atividade"
};

export const formatWorkoutActivity = (activityType: string): string => {
  const raw = activityType.trim();
  const legacy = /^WorkoutHealthValue\b/i.test(raw);
  const legacyType = legacy ? /workoutActivityType:\s*([A-Z_]+)/i.exec(raw)?.[1] : undefined;
  const code = (legacyType ?? raw).replace(/^HealthWorkoutActivityType\./i, "").toUpperCase();
  const label = workoutActivityLabels[code];
  if (label) return label;
  if (!raw || legacy || /^HealthWorkoutActivityType\./i.test(raw) || /^[A-Z][A-Z0-9_]*$/.test(raw)) {
    return "Atividade registrada";
  }
  return raw;
};

export const formatWorkoutDate = (value: string): string => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Data indisponível";
  return date.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export const formatWorkoutDuration = (workout: HealthWorkout): string =>
  formatDuration(
    Math.max(0, Math.round((new Date(workout.endedAt).getTime() - new Date(workout.startedAt).getTime()) / 60000))
  );

export const recentWorkouts = (view: HealthPocViewModel, now: Date = new Date()): HealthWorkout[] => {
  const start = new Date(now);
  start.setDate(start.getDate() - 6);
  start.setHours(0, 0, 0, 0);
  return view.workouts
    .filter(workout => {
      const startedAt = new Date(workout.startedAt).getTime();
      return Number.isFinite(startedAt) && startedAt >= start.getTime() && startedAt <= now.getTime();
    })
    .sort((left, right) => new Date(right.startedAt).getTime() - new Date(left.startedAt).getTime());
};

export const todayCards = (view: HealthPocViewModel): HealthMetricCardData[] => {
  const configs = [
    { key: "steps", label: "Passos", unit: null, icon: "heartbeat", tone: "blue" },
    { key: "distance", label: "Distância", unit: "m", icon: "location-dot", tone: "teal" },
    { key: "energy", label: "Calorias ativas", unit: "kcal", icon: "heart", tone: "orange" },
    { key: "activeMinutes", label: "Tempo ativo", unit: "min", icon: "clock", tone: "purple" }
  ];
  return configs.map(config => {
    const metric = metricByKey(view, config.key) || emptyMetric(config.key, config.label, config.unit);
    return {
      metric,
      icon: config.icon,
      tone: config.tone,
      valueLabel: config.key === "activeMinutes" ? formatDuration(metric.value) : formatMetricValue(metric),
      unitLabel: config.key === "activeMinutes" ? "" : formatMetricUnit(metric),
      caption: metric.available ? measuredAtLabel(metric) : ""
    };
  });
};

export const recoveryCards = (view: HealthPocViewModel): HealthMetricCardData[] => {
  const configs = [
    { key: "heart", label: "Frequência cardíaca", unit: "bpm", icon: "heartbeat", tone: "orange" },
    { key: "resting", label: "Frequência em repouso", unit: "bpm", icon: "heart", tone: "blue" },
    { key: "oxygen", label: "Oxigenação", unit: "%", icon: "file-waveform", tone: "teal" },
    { key: "weight", label: "Peso", unit: "kg", icon: "file-medical", tone: "purple" }
  ];
  return configs.map(config => {
    const metric = metricByKey(view, config.key) || emptyMetric(config.key, config.label, config.unit);
    return {
      metric,
      icon: config.icon,
      tone: config.tone,
      valueLabel: formatMetricValue(metric),
      unitLabel: formatMetricUnit(metric),
      caption: metric.available ? measuredAtLabel(metric) : "Sem registro no aparelho"
    };
  });
};

export const iconForMetric = (key: string): string =>
  ({
    steps: "heartbeat",
    distance: "location-dot",
    energy: "heart",
    sleep: "moon",
    heart: "heartbeat",
    resting: "heart",
    oxygen: "file-waveform",
    weight: "file-medical"
  }[key] || "hand-holding-heart");

export const toneForMetric = (key: string): string =>
  ({
    steps: "blue",
    distance: "teal",
    energy: "orange",
    sleep: "purple",
    heart: "orange",
    resting: "blue",
    oxygen: "teal",
    weight: "purple"
  }[key] || "blue");
